/* @flow */
import cx from 'classnames';
import invariant from 'invariant';
import React from 'react';
import ReactDOM from 'react-dom';

import { debounce } from 'lodash';
import { isAdmin } from 'shapes/Affiliation';
import { memberTypeFor } from 'groups/lib/groupDisplay';
import AddGroupOwnerModal from './AddGroupOwnerModal.jsx';
import AffiliationStore from 'stores/AffiliationStore';
import Button from 'components/Button.jsx';
import cookies from 'lib/Cookies';
import CurrentUser from 'auth/CurrentUser';
import FeatureStore from 'stores/FeatureStore';
import Glyph from 'components/Glyph.jsx';
import Group, { GROUP_MEMBER_PAGE_SIZE } from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupMembership from 'entities/GroupMembership';
import GroupMemberships from 'collections/GroupMemberships';
import GroupMembershipStore from 'stores/GroupMembershipStore';
import GroupMembersTabListItem from './GroupMembersTabListItem.jsx';
import GroupStore from 'groups/stores/GroupStore';
import Image from 'components/Image.jsx';
import Input from 'components/Input.jsx';
import InviteButton from 'users/components/InviteButton.jsx';
import JoinGroupModal from './JoinGroupModal.jsx';
import KeyConstants from 'constants/KeyConstants';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import Menu from 'components/Menu.jsx';
import MenuItem from 'components/MenuItem.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ScrollWatch from 'components/ScrollWatch.jsx';
import Text from 'components/Text.jsx';
import TitleHeader from 'components/TitleHeader.jsx';

import focusNode from 'utils/focusNode';
import getMatcher from 'utils/getMatcher';
import getRedirectLocationAfterGroupJoin from 'utils/getRedirectLocationAfterGroupJoin';

const SORTS = ['firstName', 'lastName', 'subscribedAt'];
const DEFAULT_SORT = 'firstName';

import './styles/GroupMembersTabList.scss';

const SortKeyCookie = {
  get: () => {
    const sort = cookies.get('GROUP_SORT_KEY');
    if (SORTS.indexOf(sort) > -1) {
      return sort;
    }

    // if undefined or unknown
    return DEFAULT_SORT;
  },

  set: (value) => {
    return cookies.set('GROUP_SORT_KEY', value);
  },
};

type Props = {
  members: GroupMemberships,
  group: Group,
};

const GroupMembersTabList = React.createClass({
  propTypes: {
    members: React.PropTypes.instanceOf(GroupMemberships).isRequired,
    group: React.PropTypes.instanceOf(Group).isRequired,
  },

  contextTypes: {
    router: React.PropTypes.object.isRequired,
  },

  getInitialState() {
    return {
      results: this.getResults(this.props.members, ''),
      query: '',
      sortKey: SortKeyCookie.get(),
      pendingQueries: 0,
    };
  },

  componentDidMount() {
    focusNode(ReactDOM.findDOMNode(this.refs.searchInput));
  },

  componentWillReceiveProps(nextProps: Props) {
    // if props changed we recompute the search results.
    this.setState({
      results: this.getResults(nextProps.members, this.state.query),
    });
  },

  getResults(members: GroupMemberships, query: string = '') {
    if (query === '') {
      return members;
    }

    const isMatch = getMatcher(query);
    return new GroupMemberships(members.filter(member => {
      const user = member.getUser();
      return isMatch([user.getFullName()]) ||
        isMatch([user.getSignature()]) ||
        isMatch([user.getNote()]);
    }));
  },

  getSortedSubscribers(key: string, members: GroupMemberships) {
    let sortedSubscribers = members;
    switch (key) {
      case 'lastName':
        sortedSubscribers = members.sortByLastName();
        break;
      case 'subscribedAt':
        sortedSubscribers = members.sortByCreatedAt();
        break;
      case 'role':
        sortedSubscribers = members.sortByOrganizationRole();
        break;
      default:
        break;
    }

    return sortedSubscribers;
  },

  allMembersLoaded() {
    const uuid = this.props.group.getUuid() || 'r';
    return GroupMembershipStore.allMembersLoadedForGroup(
      uuid,
      this.state.query
    );
  },

  handleFilterSelect(key: string) {
    SortKeyCookie.set(key);
    this.setState({ sortKey: key });
  },

  // $FlowFixMe
  handleSearchInput(event) {
    const value = event.target.value;
    if (!value) {
      this.reset();
      return;
    }
    this.updateResults(value);
  },

  // $FlowFixMe
  updateResults: debounce(function(query) {
    this.setState({
      results: this.getResults(this.props.members, query),
      query,
    }, () => {
      if (!this.allMembersLoaded()) {
        this.loadMoreSubscribers();
      }
    });
  }, 300),

  // $FlowFixMe
  handleSearchKeyDown(event) {
    const code = event.keyCode;
    switch (code) {
      case KeyConstants.ESCAPE:
        this.reset();
        break;
      default:
        return;
    }
    event.preventDefault();
  },

  shouldShowNullState() {
    return this.props.members.length < 2;
  },

  shouldShowAdminLink() {
    const { group } = this.props;

    if (!FeatureStore.hasFeature('web_admin_console')) {
      return false;
    }

    if (!group.isStaffClass()) {
      return false;
    }

    const organization = this.props.group.getOrganization();

    if (!organization) {
      console.error('error - staff class should always have an oganization.');
      return false;
    }

    const affiliation = AffiliationStore.getAffiliation(organization.id);

    if (!affiliation) {
      console.error('error - staff class without an affiliation.');
      return false;
    }

    return isAdmin(affiliation);
  },

  hasPendingQueries() {
    return this.state.pendingQueries > 0;
  },

  loadMoreSubscribers() {
    this.setState({
      pendingQueries: this.state.pendingQueries + 1,
    });
    const numResults = this.state.results.length;
    const page = Math.floor(numResults / GROUP_MEMBER_PAGE_SIZE) + 1;
    const { group } = this.props;
    const loadPromise = group.isRoot() ?
      GroupActions.loadRootGroupMemberships({
        page,
        perPage: GROUP_MEMBER_PAGE_SIZE,
        query: this.state.query,
      }) :
      GroupActions.loadGroupMemberships(this.props.group, {
        page,
        perPage: GROUP_MEMBER_PAGE_SIZE,
        query: this.state.query,
      });

    loadPromise.then(() => this.setState({
      pendingQueries: this.state.pendingQueries - 1,
    })).catch(e => console.error('GroupMembersTabList loadPromise failed', e));
  },

  loadingIndicatorEnter() {
    if (!this.hasPendingQueries() && !this.allMembersLoaded()) {
      this.loadMoreSubscribers();
    }
  },

  reset() {
    this.setState({
      results: this.props.members,
      query: '',
    });
  },

  waitingOnQueries() {
    return this.hasPendingQueries() && !this.allMembersLoaded();
  },

  handleGroupJoined(group: Group) {
    this.context.router.push(getRedirectLocationAfterGroupJoin(group));
  },

  renderSearchBar() {
    const { group } = this.props;
    const placeholder = `Search ${memberTypeFor(group)}`;

    return (
      <div className="GroupMembersTabList-top">
        <div className="GroupMembersTabList-actions">
          <div className="GroupMembersTabList-actions-search">
            <div className="GroupMembersTabList-actions-search-inputWrap">
              <Input
                ref="searchInput"
                className="GroupMembersTabList-actions-search-input"
                type="text"
                size="medium"
                placeholder={placeholder}
                onInput={this.handleSearchInput}
                onKeyDown={this.handleSearchKeyDown}
              />
              <Glyph
                className="GroupMembersTabList-actions-search-inputGlyph"
                name="search"
                size="medium"
              />
            </div>
          </div>
          {this.renderSortButton()}
        </div>
      </div>
    );
  },

  renderColumnHeaders() {
    const { group } = this.props;
    const showRoles = group.getOrganization();

    return (
      <tr>
        <th className="GroupMembersTabList-name">
          <div className="GroupMembersTabList-nameHeader">
            Name
          </div>
        </th>
        {showRoles && <th className="GroupMembersTabList-role">Roles</th>}
        {
          !group.isRoot() && !group.isStaffClass() /* createdAt is inaccurate for staff classes */ &&
            <th className="GroupMembersTabList-date">Date joined</th>
        }
        <th className="GroupMembersTabList-actionsHeader" />
      </tr>
    );
  },

  renderSortButton() {
    const { group } = this.props;
    const menu = (
      <Menu selectedKey={this.state.sortKey} onSelect={this.handleFilterSelect}>
        <MenuItem key="firstName">By first name</MenuItem>
        <MenuItem key="lastName">By last name</MenuItem>
        {!group.isRoot() && <MenuItem key="subscribedAt">By most recently joined</MenuItem>}
        {group.getOrganization() && <MenuItem key="role">By role</MenuItem>}
      </Menu>
    );

    const tooltip = `Sort ${memberTypeFor(this.props.group)}`;

    return (
      <LayerTrigger
        layer={menu}
        trigger="click"
        placement="bottom"
        alignment="right">
        <Button
          className="GroupMembersTabList-filterButton"
          level="text"
          size="small"
          tooltip={tooltip}
          tooltipPlacement="bottom"
          eventElementName="GroupMembersTabList-filterButton">
          <Glyph name="sort" size="large" solo />
        </Button>
      </LayerTrigger>
    );
  },

  renderLoadingIndicator() {
    return (
      <ScrollWatch onEnter={this.loadingIndicatorEnter} threshold={0.25}>
        <div className="ChatList-loading">
          <LoadingIndicator size="small" />
        </div>
      </ScrollWatch>
    );
  },

  renderNullState() {
    const { group } = this.props;
    const search = !this.waitingOnQueries() && this.state.results.length === 0;

    let title = null;
    let subtitle = null;
    let button = (
      <InviteButton level="secondary" group={group} forceType="button" />
    );

    if (group.isRoot()) {
      if (GroupStore.getGroups().length === 0) {
        title = 'Remind is better with other people';
        subtitle = 'Connect with others at your school.';
        button = (
          <ModalTrigger modal={<JoinGroupModal onJoin={this.handleGroupJoined} />}>
            <Button level="secondary">Join a class</Button>
          </ModalTrigger>
        );
      } else {
        title = 'Remind is better with other people';
        subtitle = 'It’s a little lonely around here. Why not add some participants?';
      }
    } else {
      title = 'Remind is better with other people';
      subtitle = `${group.getName()} feels a little lonely. Why not add some participants?`;
    }

    if (search && this.state.query.length > 0) {
      title = 'No participants matched your search.';
      subtitle = '';
      button = null;
    }

    return (
      <div className="GroupMembersTabList-empty">
        <TitleHeader
          image={require('assets/images/GroupMembersTabList/participants.svg')}
          title={title}
          subtext={subtitle}
          action={button}
        />
      </div>
    );
  },

  renderResults() {
    const searchReturnedNoResults = !this.waitingOnQueries() && this.state.results.length === 0;
    const results = this.getSortedSubscribers(this.state.sortKey, this.state.results);

    const noResultsNull = (
      <div className="GroupMembersTabList-noSearchResultsWrap">
        {this.renderNullState()}
      </div>
    );

    return (
      <div className="GroupMembersTabList-table-wrap">
        <table className="GroupMembersTabList-table">
          <thead>
            {this.renderColumnHeaders()}
          </thead>
          <tbody>
            <tr>
              <td colSpan="4">
                {this.renderSearchBar()}
              </td>
            </tr>
            {results.map(this.renderSubscriber)}
          </tbody>
        </table>
        {searchReturnedNoResults && noResultsNull}
        {!this.allMembersLoaded() && this.renderLoadingIndicator()}
      </div>
    );
  },

  renderSubscriber(member: GroupMembership, index: number) {
    const id = member.getUser().id;

    return (
      <GroupMembersTabListItem
        ref={`searchItem-${index}`}
        className="GroupMembersTabList-tableItem"
        key={id}
        member={member}
        isLinkedToOrg={!!this.props.group.getOrganization()}
        query={this.state.query}
        displayBy={this.state.sortKey}
        group={this.props.group}
      />
    );
  },

  renderHeader(group: Group) {
    const showInviteButton = group.canAddPeople() || group.isRoot();
    const membersCount = group.getMembershipsCount();
    const showMemberCount =
      !group.isRoot() && // not viewing 'all classes'
      membersCount > 2 && // more than one subscriber (yourself)
      (group.isOwnedByUser(CurrentUser.getUserId()) || group.isSearchable()); // group is searchable, or you own it

    return (
      <div className="GroupMembersTabList-header">
        <div className="GroupMembersTabList-header-wrap">
          <div className="GroupMembersTabList-header-title">
            <h1>
              <Text size="large">
                {'People '}
                {showMemberCount && <Text color="thunder">({membersCount})</Text>}
              </Text>
            </h1>
          </div>
          <div className="GroupMembersTabList-header-actions">
            {
              group.canAddOwner() &&
                <ModalTrigger modal={<AddGroupOwnerModal group={group} />}>
                  <Button
                    level="secondary"
                    size="small"
                    className="GroupMembersTabList-header-actions-addOwner"
                    eventElementName="GroupMembersTabList-header-actions-addOwner">
                    Add owner
                  </Button>
                </ModalTrigger>
            }
            {
              showInviteButton &&
                <InviteButton
                  group={group}
                  forceType="button"
                />
            }
          </div>
        </div>
      </div>
    );
  },

  renderAdminLink() {
    const org = this.props.group.getOrganization();
    invariant(org, 'must have an org to render the admin link');
    return (
      <div className="GroupMembersTabList-admin-link">
        <Image
          height={32}
          width={27}
          className="GroupMembersTabList-admin-link-image"
          src={require('assets/images/admin/infographics.svg')}
        />
        Manage your school more easily by trying the
        {' '}
        <Link
          eventElementName="go_to_admin_console_people_tab"
          href={`/admin/schools/${org.id}/information/people`}
          target="_blank">
          Admin Console
        </Link>.
      </div>
    );
  },

  render() {
    const { group } = this.props;
    const showNullState = this.shouldShowNullState();
    const showHeader = !showNullState; // hide header in null state

    return (
      <div
        className={cx('GroupMembersTabList', this.props.className)}>
        {showHeader && this.renderHeader(group)}
        <div className="GroupMembersTabList-content">
          <div className="GroupMembersTabList-content-body">
            {this.shouldShowAdminLink() && this.renderAdminLink()}
            {showNullState ? this.renderNullState() : this.renderResults()}
          </div>
        </div>
      </div>
    );
  },
});

export default GroupMembersTabList;



// WEBPACK FOOTER //
// ./src/groups/components/GroupMembersTabList.jsx